import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

function EmiDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [emi, setEmi] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  const [returnLoading, setReturnLoading] = useState(false);

  useEffect(() => {
    const fetchEmiDetails = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/emi/${id}`);
        setEmi(response.data);
        setProduct(response.data.product);
        
        // Try to find customer by phone number
        if (response.data.customer && response.data.customer.phone) {
          fetchCustomerByPhone(response.data.customer.phone);
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load EMI details');
        setLoading(false);
        console.error('Error fetching EMI details:', err);
      }
    };

    fetchEmiDetails();
  }, [id]);

  const fetchCustomerByPhone = async (phone) => {
    try {
      setCustomerLoading(true);
      const response = await axios.get(`${config.API_URL}/customers/search/phone/${phone}`);
      if (response.data && response.data.length > 0) {
        setCustomerData(response.data[0]);
      }
      setCustomerLoading(false);
    } catch (err) {
      console.error('Error fetching customer data:', err);
      setCustomerLoading(false);
    }
  };

  const calculateTotalPaid = () => {
    if (!emi || !emi.payments) return 0;
    
    return emi.payments.reduce((total, payment) => {
      if (payment.status === 'completed') {
        return total + payment.amount;
      }
      return total;
    }, 0);
  };
  
  // Calculate EMI amount paid (excluding down payment)
  const calculateEmiPaid = () => {
    if (!emi || !emi.payments) return 0;
    
    return emi.payments.reduce((total, payment, index) => {
      // Skip the first payment (down payment) and only count completed payments
      if (payment.status === 'completed' && index > 0) {
        return total + payment.amount;
      }
      return total;
    }, 0);
  };

  const calculateRemainingAmount = () => {
    if (!emi) return 0;
    
    // Total amount includes down payment + EMI payments
    const totalAmount = emi.emiPlan.totalAmount;
    
    // Total paid so far (including down payment)
    const totalPaid = calculateTotalPaid();
    
    // Calculate unpaid late fees from the database
    const unpaidLateFees = (emi.lateFees || [])
      .filter(fee => !fee.isPaid)
      .reduce((sum, fee) => sum + (fee.amount || 0), 0);
    
    // Current month late fee (if any)
    const currentLateFee = calculateLateFee();
    
    // Total late fees = unpaid late fees + current month late fee
    const totalLateFees = unpaidLateFees + currentLateFee;
    
    // Remaining = total + late fees - paid
    return totalAmount + totalLateFees - totalPaid;
  };

  const calculateLateFee = () => {
    if (!emi) return 0;
    
    // Use the currentLateFeeAmount virtual property if available
    if (emi.currentLateFeeAmount !== undefined) {
      return emi.currentLateFeeAmount;
    }
    
    // Calculate days late manually if virtual not available
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Check if payment was already made for this month
    const paymentMadeThisMonth = emi.payments.some(payment => 
      payment.status === 'completed' &&
      !payment.isLateFee &&
      new Date(payment.date).getMonth() === currentMonth &&
      new Date(payment.date).getFullYear() === currentYear
    );
    
    if (paymentMadeThisMonth || emi.status !== 'active') {
      return 0;
    }
    
    // Calculate the due date for current month
    const dueDate = new Date(currentYear, currentMonth, emi.emiPlan.preferredPaymentDate || 1);
    const gracePeriodDays = emi.emiPlan.gracePeriodDays || 2;
    const gracePeriodEndDate = new Date(dueDate);
    gracePeriodEndDate.setDate(dueDate.getDate() + gracePeriodDays);
    
    // If current date is before grace period end, no late fee
    if (now <= gracePeriodEndDate) {
      return 0;
    }
    
    // Calculate days late after grace period
    const timeDiff = now - gracePeriodEndDate;
    const daysLate = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const lateFeePerDay = emi.emiPlan.lateFeePerDay || 15;
    
    return daysLate * lateFeePerDay;
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    setPaymentLoading(true);
    
    try {
      // Round amount to 2 decimals or round up if there are decimals
      let amount = parseFloat(paymentAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid payment amount');
      }
      
      // Round to 2 decimal places, or round up to nearest rupee if there are decimals
      const decimalPart = amount % 1;
      if (decimalPart > 0) {
        // If there are decimals, round up to nearest rupee
        amount = Math.ceil(amount);
      } else {
        // Otherwise, just round to 2 decimals
        amount = Math.round(amount * 100) / 100;
      }
      
      // Ask for payment date
      const paymentDate = prompt('Enter payment date (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
      if (!paymentDate) {
        throw new Error('Payment date is required');
      }
      
      // Check if this is a late fee payment
      const isLateFee = e.target.dataset.type === 'latefee';
      
      await axios.post(`${config.API_URL}/emi/${id}/payments`, { 
        amount,
        isLateFee,
        date: paymentDate
      });
      
      // Refresh EMI details
      const response = await axios.get(`${config.API_URL}/emi/${id}`);
      setEmi(response.data);
      setProduct(response.data.product);
      
      // Update customer's EMI purchase record if customer exists
      if (customerData) {
        // No need to update purchase record for payments, as they don't affect the core purchase
        // Just refresh customer data to get latest reward points
        fetchCustomerByPhone(customerData.phone);
      }
      
      setPaymentAmount('');
      setShowPaymentForm(false);
      alert('Payment recorded successfully');
    } catch (err) {
      setError('Failed to record payment: ' + err.message);
      console.error('Error recording payment:', err);
    } finally {
      setPaymentLoading(false);
    }
  };

  const linkEmiToCustomer = async () => {
    if (!emi || !customerData) return;
    
    try {
      // Calculate profit for reward points
      const profit = calculateProfit();
      
      // Add the EMI purchase to customer's purchase history
      await axios.post(`${config.API_URL}/customers/${customerData._id}/purchases`, {
        date: new Date(emi.startDate),
        productId: emi.product,
        productName: emi.productInfo.name,
        variant: emi.productInfo.variant || '',
        salePrice: emi.saleInfo.salePrice,
        isEmi: true,
        emiId: emi._id,
        profit: profit > 0 ? profit : 0
      });
      
      alert('EMI purchase linked to customer profile successfully');
      
      // Refresh customer data
      fetchCustomerByPhone(customerData.phone);
    } catch (err) {
      setError('Failed to link EMI to customer: ' + err.message);
      console.error('Error linking EMI to customer:', err);
    }
  };

  const openReturnModal = () => {
    setRefundAmount('');
    setReturnNotes('');
    setShowReturnModal(true);
  };

  const getReturnPreview = () => {
    const totalCollected = calculateTotalPaid();
    const refund = parseFloat(refundAmount);
    const safeRefund = Number.isFinite(refund) && refund >= 0 ? refund : 0;
    const netKept = Math.round((totalCollected - safeRefund) * 100) / 100;
    const expectedProfit = calculateProfit();
    const profitAdjustment = Math.round((netKept - expectedProfit) * 100) / 100;
    return { totalCollected, refund: safeRefund, netKept, expectedProfit, profitAdjustment };
  };

  const handleReturnDevice = async () => {
    if (!emi) return;
    const preview = getReturnPreview();
    if (!Number.isFinite(parseFloat(refundAmount)) || parseFloat(refundAmount) < 0) {
      alert('Valid refund amount daalein (0 bhi chalega)');
      return;
    }
    if (preview.refund > preview.totalCollected) {
      alert('Refund collected amount se zyada nahi ho sakta');
      return;
    }

    const confirmMsg =
      `Phone return & EMI close?\n\n` +
      `Customer ne diya: ₹${preview.totalCollected.toLocaleString('en-IN')}\n` +
      `Refund: ₹${preview.refund.toLocaleString('en-IN')}\n` +
      `Net kept: ₹${preview.netKept.toLocaleString('en-IN')}\n` +
      `Expected profit (sale): ₹${preview.expectedProfit.toLocaleString('en-IN')}\n` +
      `Sale-month Return adj.: ₹${preview.profitAdjustment.toLocaleString('en-IN')}\n\n` +
      `EMI dashboard se hat jayega, phone stock mein wapas.`;

    if (!window.confirm(confirmMsg)) return;

    setReturnLoading(true);
    try {
      const response = await axios.post(`${config.API_URL}/emi/${emi._id}/return-device`, {
        refundAmount: preview.refund,
        notes: returnNotes.trim()
      });
      alert(response.data.message || 'Return close successful');
      setShowReturnModal(false);
      const refreshed = await axios.get(`${config.API_URL}/emi/${id}`);
      setEmi(refreshed.data);
      setProduct(refreshed.data.product);
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Return close failed');
    } finally {
      setReturnLoading(false);
    }
  };

  const getDueDateString = () => {
    if (!emi) return '';
    
    const nextPaymentDate = new Date(emi.nextPaymentDate);
    return nextPaymentDate.toLocaleDateString();
  };

  const getDaysLate = () => {
    if (!emi) return 0;
    
    if (emi.daysLateForCurrentMonth !== undefined) {
      return emi.daysLateForCurrentMonth;
    }
    
    // Calculate days late manually if virtual not available
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Calculate the due date for current month
    const dueDate = new Date(currentYear, currentMonth, emi.emiPlan.preferredPaymentDate || 1);
    const gracePeriodDays = emi.emiPlan.gracePeriodDays || 2;
    const gracePeriodEndDate = new Date(dueDate);
    gracePeriodEndDate.setDate(dueDate.getDate() + gracePeriodDays);
    
    // If current date is before grace period end, no late days
    if (now <= gracePeriodEndDate) {
      return 0;
    }
    
    // Calculate days late after grace period
    const timeDiff = now - gracePeriodEndDate;
    return Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const calculateProfit = () => {
    if (!emi) return 0;
    
    // Use virtual if available
    if (emi.profit !== undefined) {
      return emi.profit;
    }
    
    // Calculate manually if not available
    const sellingPrice = emi.saleInfo?.salePrice || emi.productInfo?.sellingPrice || 0;
    const buyingPrice = emi.productInfo?.buyingPrice || 0;
    return sellingPrice - buyingPrice;
  };

  const calculateRewardPoints = () => {
    const profit = calculateProfit();
    return Math.round(profit * 0.1);
  };

  // Check if this EMI is already linked to the customer
  const isEmiLinkedToCustomer = () => {
    if (!customerData || !customerData.purchases || !emi) return false;
    
    return customerData.purchases.some(purchase => 
      purchase.isEmi && purchase.emiId === emi._id
    );
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (!emi) {
    return (
      <div className="alert alert-warning" role="alert">
        EMI plan not found
      </div>
    );
  }

  const totalPaid = calculateTotalPaid();
  const remainingAmount = calculateRemainingAmount();
  const lateFee = calculateLateFee();
  
  // Calculate EMI payment percentage (excluding down payment)
  const downPayment = emi.emiPlan.downPayment || 0;
  const emiTotal = emi.emiPlan.totalAmount - downPayment;
  const emiPaid = calculateEmiPaid();
  const paymentPercentage = emiTotal > 0 ? Math.round((emiPaid / emiTotal) * 100) : 0;

  return (
    <div className="emi-detail-container">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h2>EMI Details</h2>
        <div className="d-flex gap-2 flex-wrap">
          {['active', 'defaulted'].includes(emi.status) && (
            <button type="button" className="btn btn-warning" onClick={openReturnModal}>
              Phone return &amp; close
            </button>
          )}
          <button onClick={() => navigate(-1)} className="btn btn-secondary">
            Back
          </button>
        </div>
      </div>

      {emi.status === 'returned' && emi.returnSettlement && (
        <div className="alert alert-warning">
          <strong>Device returned — case closed.</strong>
          {' '}Refund ₹{(emi.returnSettlement.refundAmount || 0).toLocaleString('en-IN')}
          · Net kept ₹{(emi.returnSettlement.netKept || 0).toLocaleString('en-IN')}
          · Sale-month adj ₹{(emi.returnSettlement.profitAdjustment || 0).toLocaleString('en-IN')}
          {emi.returnSettlement.restocked ? ' · Phone restocked' : ' · Restock pending'}
        </div>
      )}

      {/* Customer Information Section */}
      <div className="card mb-4">
        <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Customer Information</h5>
          {customerData && (
            <Link to={`/customer/${customerData._id}`} className="btn btn-sm btn-light">
              View Full Profile
            </Link>
          )}
        </div>
        <div className="card-body">
          {customerLoading ? (
            <div className="text-center">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="ms-2">Loading customer data...</span>
            </div>
          ) : customerData ? (
            <div className="row">
              <div className="col-md-7">
                <table className="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr>
                      <th width="25%">Name:</th>
                      <td>{customerData.name}</td>
                    </tr>
                    <tr>
                      <th>Phone:</th>
                      <td>{customerData.phone}</td>
                    </tr>
                    <tr>
                      <th>Email:</th>
                      <td>{customerData.email || 'Not provided'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="col-md-5">
                <div className="d-flex align-items-center">
                  <div className="text-center me-4">
                    <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Total Purchases</h6>
                    <h5 className="mb-0">{customerData.totalPurchases || 0}</h5>
                  </div>
                  <div className="text-center">
                    <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Reward Points</h6>
                    <h5 className="mb-0">{customerData.rewardPoints || 0}</h5>
                  </div>
                </div>
                
                {!isEmiLinkedToCustomer() && (
                  <div className="mt-3 text-end">
                    <button 
                      className="btn btn-sm btn-warning" 
                      onClick={linkEmiToCustomer}
                    >
                      Link EMI to Customer Profile
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="alert alert-warning">
                <strong>No customer profile found.</strong> The customer information from the EMI record is:
              </div>
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <th width="20%">Name:</th>
                    <td>{emi.customer.name}</td>
                  </tr>
                  <tr>
                    <th>Phone:</th>
                    <td>{emi.customer.phone}</td>
                  </tr>
                  <tr>
                    <th>Email:</th>
                    <td>{emi.customer.email || 'Not provided'}</td>
                  </tr>
                  <tr>
                    <th>Address:</th>
                    <td>{emi.customer.address || 'Not provided'}</td>
                  </tr>
                  <tr>
                    <th>ID Proof:</th>
                    <td>{emi.customer.idProof || 'Not provided'}</td>
                  </tr>
                </tbody>
              </table>
              <div className="mt-3">
                <Link to="/customers" className="btn btn-primary btn-sm">
                  Create Customer Profile
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Product & EMI Details</h4>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <h5>Product Information</h5>
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <th>Product</th>
                    <td>{product ? product.name : 'Unknown Product'}</td>
                  </tr>
                  <tr>
                    <th>Price</th>
                    <td>₹{product ? product.price.toLocaleString() : '-'}</td>
                  </tr>
                  <tr>
                    <th>Sale Price</th>
                    <td>₹{emi.saleInfo?.salePrice?.toLocaleString() || '-'}</td>
                  </tr>
                  <tr>
                    <th>Variant</th>
                    <td>{emi.productInfo?.variant?.includes(':') ? emi.productInfo.variant.split(':')[1].trim() : emi.productInfo?.variant || '-'}</td>
                  </tr>
                  {/* IMEI Details for Mobile Products */}
                  {emi.imeiInfo && (emi.imeiInfo.imei1 || emi.imeiInfo.imei2) && (
                    <>
                      <tr>
                        <th>📱 IMEI 1</th>
                        <td>{emi.imeiInfo.imei1 || '-'}</td>
                      </tr>
                      <tr>
                        <th>📱 IMEI 2</th>
                        <td>{emi.imeiInfo.imei2 || '-'}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <h5>EMI Plan</h5>
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <th>EMI Plan</th>
                    <td>{emi.emiPlan.months} months @ {emi.emiPlan.interestRate}% interest</td>
                  </tr>
                  <tr>
                    <th>Down Payment</th>
                    <td>₹{emi.emiPlan.downPayment.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <th>Monthly EMI</th>
                    <td>₹{emi.emiPlan.monthlyAmount.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <th>Preferred Payment Date</th>
                    <td>{emi.emiPlan.preferredPaymentDate || 1}{getOrdinalSuffix(emi.emiPlan.preferredPaymentDate || 1)} of each month</td>
                  </tr>
                  <tr>
                    <th>Grace Period</th>
                    <td>{emi.emiPlan.gracePeriodDays || 2} days (no late fee during grace period)</td>
                  </tr>
                  <tr>
                    <th>Late Fee</th>
                    <td>₹{emi.emiPlan.lateFeePerDay || 15} per day after grace period</td>
                  </tr>
                  <tr>
                    <th>Reward Points</th>
                    <td className="text-primary">{calculateRewardPoints()} points</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card">
            <div className="card-body text-center">
              <h6>Monthly Payment</h6>
              <h3 className="text-primary">₹{emi.emiPlan.monthlyAmount.toLocaleString()}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body text-center">
              <h6>Total Amount</h6>
              <h3>₹{emi.emiPlan.totalAmount.toLocaleString()}</h3>
              <small className="text-muted">Sale Price + Interest</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body text-center">
              <h6>Down Payment</h6>
              <h3>₹{emi.emiPlan.downPayment.toLocaleString()}</h3>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body text-center">
              <h6>EMI Paid</h6>
              <h3 className="text-success">₹{calculateEmiPaid().toLocaleString()}</h3>
              <small className="text-muted">Excluding Down Payment</small>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-body text-center">
              <h6>Remaining Balance</h6>
              <h3 className={remainingAmount > 0 ? "text-danger" : "text-success"}>₹{remainingAmount.toLocaleString()}</h3>
            </div>
          </div>
        </div>
      </div>
      
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Payment Progress</h5>
          <span className="badge bg-info">{paymentPercentage}% Complete</span>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <h6>EMI Payment Progress (excluding down payment)</h6>
              <div className="progress" style={{ height: '25px' }}>
                <div 
                  className="progress-bar progress-bar-striped progress-bar-animated" 
                  role="progressbar" 
                  style={{ width: `${paymentPercentage}%` }}
                  aria-valuenow={paymentPercentage} 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                >
                  {paymentPercentage}%
                </div>
              </div>
              <div className="mt-2 small text-muted">
                <strong>EMI Amount:</strong> ₹{(emi.emiPlan.totalAmount - emi.emiPlan.downPayment).toLocaleString()} | 
                <strong> Paid:</strong> ₹{calculateEmiPaid().toLocaleString()} | 
                <strong> Remaining:</strong> ₹{(emi.emiPlan.totalAmount - emi.emiPlan.downPayment - calculateEmiPaid()).toLocaleString()}
              </div>
            </div>
            <div className="col-md-6">
              <h6>Overall Payment Progress (including down payment)</h6>
              <div className="progress" style={{ height: '25px' }}>
                <div 
                  className="progress-bar bg-success progress-bar-striped" 
                  role="progressbar" 
                  style={{ width: `${Math.round((calculateTotalPaid() / emi.emiPlan.totalAmount) * 100)}%` }}
                  aria-valuenow={Math.round((calculateTotalPaid() / emi.emiPlan.totalAmount) * 100)} 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                >
                  {Math.round((calculateTotalPaid() / emi.emiPlan.totalAmount) * 100)}%
                </div>
              </div>
              <div className="mt-2 small text-muted">
                <strong>Total Amount:</strong> ₹{emi.emiPlan.totalAmount.toLocaleString()} | 
                <strong> Paid:</strong> ₹{calculateTotalPaid().toLocaleString()} | 
                <strong> Late Fees:</strong> ₹{((emi.lateFees || []).filter(fee => !fee.isPaid).reduce((sum, fee) => sum + (fee.amount || 0), 0) + calculateLateFee()).toLocaleString()} | 
                <strong> Remaining:</strong> ₹{remainingAmount.toLocaleString()}
              </div>
            </div>
          </div>
          
          {(() => {
            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();
            const dueDate = new Date(currentYear, currentMonth, emi.emiPlan.preferredPaymentDate || 1);
            const gracePeriodDays = emi.emiPlan.gracePeriodDays || 2;
            const gracePeriodEndDate = new Date(dueDate);
            gracePeriodEndDate.setDate(dueDate.getDate() + gracePeriodDays);
            
            // Check if payment is due but within grace period
            const isWithinGracePeriod = now > dueDate && now <= gracePeriodEndDate;
            
            return (
              <>
                {isWithinGracePeriod && (
                  <div className="alert alert-warning mb-4">
                    <h5>⚠️ Payment Due - Grace Period Active</h5>
                    <p>Your payment was due on {dueDate.toLocaleDateString()}, but you have until {gracePeriodEndDate.toLocaleDateString()} to pay without any late fees.</p>
                    <p><strong>Grace Period:</strong> {emi.emiPlan.gracePeriodDays || 2} days | <strong>Late Fee:</strong> ₹{emi.emiPlan.lateFeePerDay || 15} per day after grace period</p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowPaymentForm('regular')}
                    >
                      Pay EMI Now
                    </button>
                  </div>
                )}
                
                                {lateFee > 0 && (
                  <div className="alert alert-danger mb-4">
                    <h5>Late Fee Due: ₹{lateFee.toLocaleString()}</h5>
                    <p>You are {getDaysLate()} days late on your payment (after {emi.emiPlan.gracePeriodDays || 2} days grace period).</p>
                    <p>Late fee is charged at ₹{emi.emiPlan.lateFeePerDay || 15} per day after grace period.</p>
                    <p>Payment due date: {getDueDateString()} | Grace period ended: {(() => {
                      const dueDate = new Date(new Date().getFullYear(), new Date().getMonth(), emi.emiPlan.preferredPaymentDate || 1);
                      const graceEndDate = new Date(dueDate);
                      graceEndDate.setDate(dueDate.getDate() + (emi.emiPlan.gracePeriodDays || 2));
                      return graceEndDate.toLocaleDateString();
                    })()}</p>
                    <button 
                      className="btn btn-warning me-2"
                      onClick={() => setShowPaymentForm('latefee')}
                    >
                      Pay Late Fee
                    </button>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowPaymentForm('regular')}
                    >
                      Pay EMI + Late Fee
                    </button>
                  </div>
                )}
              </>
            );
          })()}
          
          {showPaymentForm && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  {showPaymentForm === 'latefee' ? 'Record Late Fee Payment' : 'Record EMI Payment'}
                </h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleRecordPayment} data-type={showPaymentForm}>
                  <div className="mb-3">
                    <label htmlFor="paymentAmount" className="form-label">Payment Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      id="paymentAmount"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder={showPaymentForm === 'latefee' 
                        ? `Recommended: ₹${Math.ceil(lateFee).toLocaleString()}` 
                        : (() => {
                            const remaining = calculateRemainingAmount();
                            const monthly = emi.emiPlan.monthlyAmount;
                            // If remaining is less than monthly, show remaining (rounded up), else show monthly (rounded up)
                            const recommended = remaining < monthly ? Math.ceil(remaining) : Math.ceil(monthly);
                            return `Recommended: ₹${recommended.toLocaleString()}`;
                          })()}
                      required
                    />
                    <small className="text-muted">
                      {(() => {
                        const remaining = calculateRemainingAmount();
                        if (remaining <= 0) return 'Loan fully paid';
                        const roundedRemaining = Math.ceil(remaining);
                        if (roundedRemaining !== remaining) {
                          return `Remaining: ₹${remaining.toFixed(2)} (Pay ₹${roundedRemaining} to fully pay)`;
                        }
                        return `Remaining: ₹${remaining.toFixed(2)}`;
                      })()}
                    </small>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary me-2"
                      onClick={() => setShowPaymentForm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={paymentLoading}
                    >
                      {paymentLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Processing...
                        </>
                      ) : (
                        'Record Payment'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
          
          {emi.status === 'completed' && (
            <div className="alert alert-success text-center">
              <h5 className="mb-0">This EMI plan has been fully paid!</h5>
            </div>
          )}
        </div>
      </div>
      
      <div className="card">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">Payment History</h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-bordered">
              <thead className="table-dark">
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Payment Method</th>
                </tr>
              </thead>
              <tbody>
                {emi.payments.length > 0 ? (
                  emi.payments.map((payment, index) => {
                    const paymentDate = new Date(payment.date);
                    return (
                      <tr key={index}>
                        <td>{paymentDate.toLocaleDateString()}</td>
                        <td>₹{payment.amount.toLocaleString()}</td>
                        <td>
                          {payment.isLateFee ? (
                            <span className="badge bg-danger">Late Fee</span>
                          ) : (
                            index === 0 ? (
                              <span className="badge bg-info">Down Payment</span>
                            ) : (
                              <span className="badge bg-primary">EMI</span>
                            )
                          )}
                        </td>
                        <td>
                          <span className={`badge ${payment.status === 'completed' ? 'bg-success' : 'bg-warning'}`}>
                            {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                          </span>
                        </td>
                        <td>
                          {payment.paymentMethod ? (
                            payment.paymentMethod.charAt(0).toUpperCase() + payment.paymentMethod.slice(1)
                          ) : (
                            'Cash'
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">No payment records found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showReturnModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-warning">
                <h5 className="modal-title">Phone return &amp; EMI close</h5>
                <button type="button" className="btn-close" onClick={() => setShowReturnModal(false)} disabled={returnLoading} />
              </div>
              <div className="modal-body">
                <p className="small text-muted mb-3">
                  Customer phone wapas karega, aap refund doge, EMI case close — sale wale month ka profit
                  Return adj. se adjust hoga (double entry nahi).
                </p>
                {(() => {
                  const p = getReturnPreview();
                  return (
                    <div className="table-responsive mb-3">
                      <table className="table table-sm">
                        <tbody>
                          <tr><th>Customer ne diya</th><td>₹{p.totalCollected.toLocaleString('en-IN')}</td></tr>
                          <tr><th>Expected profit (sale)</th><td>₹{p.expectedProfit.toLocaleString('en-IN')}</td></tr>
                          <tr><th>Net kept (after refund)</th><td className="text-success">₹{p.netKept.toLocaleString('en-IN')}</td></tr>
                          <tr>
                            <th>Sale-month Return adj.</th>
                            <td className={p.profitAdjustment >= 0 ? 'text-success' : 'text-danger'}>
                              ₹{p.profitAdjustment.toLocaleString('en-IN')}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
                <div className="mb-3">
                  <label className="form-label">Refund amount (₹)</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    step="1"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    placeholder="e.g. 12000"
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Notes (optional)</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={returnNotes}
                    onChange={(e) => setReturnNotes(e.target.value)}
                    placeholder="Condition / reason"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowReturnModal(false)} disabled={returnLoading}>
                  Cancel
                </button>
                <button type="button" className="btn btn-warning" onClick={handleReturnDevice} disabled={returnLoading}>
                  {returnLoading ? 'Closing...' : 'Confirm return & close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmiDetail; 